// src/components/timeReconcile/reconcileRules.js
//
// React-free helpers for the Missing Time tab: the default range, span
// validation, chip filtering, date grouping and the prefill builders that feed
// the existing To-Do ticket modal and the Add Time modal. Everything here is
// pure and tested in reconcileRules.test.js.
//
// Dates are the BROWSER's local calendar (Craig's machine is on Perth time),
// matching how the To-Do ticket modal already dates its time entry. The
// backend does its own Perth → UTC conversion exactly once; nothing here adds
// an offset to a value the server produced.

export const MAX_RANGE_DAYS = 31;

export const STATUS_META = {
  covered:            { label: "Covered",          color: "var(--ops)",    bg: "rgba(39,174,96,0.12)" },
  "possibly-covered": { label: "Possibly covered", color: "var(--warn)",   bg: "rgba(230,126,34,0.14)" },
  "ticket-no-time":   { label: "Ticket, no time",  color: "var(--cal)",    bg: "rgba(211,84,0,0.12)" },
  nothing:            { label: "Nothing logged",   color: "var(--danger)", bg: "rgba(192,57,43,0.12)" },
  unresolved:         { label: "No company",       color: "var(--text3)",  bg: "rgba(0,0,0,0.06)" },
};

export const SOURCE_LABEL = { contact: "Contact", ticketNumber: "Ticket #", domain: "Domain", manual: "Manual" };

export const DISMISS_REASONS = [
  { id: "internal",        label: "Internal" },
  { id: "not-billable",    label: "Not billable" },
  { id: "personal",        label: "Personal" },
  { id: "logged-elsewhere", label: "Logged elsewhere" },
  { id: "other",           label: "Other" },
];

export const CHIPS = [
  { id: "attention", label: "Needs attention" },
  { id: "covered",   label: "Covered" },
  { id: "dismissed", label: "Dismissed" },
  { id: "all",       label: "All" },
];

const ATTENTION = new Set(["ticket-no-time", "nothing", "unresolved", "possibly-covered"]);

const pad = (n) => String(n).padStart(2, "0");

/** Local calendar date of a Date, as YYYY-MM-DD. */
export function ymdLocal(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** YYYY-MM-DD plus n days, in the local calendar. */
export function addDays(ymd, n) {
  const [y, m, d] = String(ymd).split("-").map(Number);
  const dt = new Date(y, m - 1, d + n);
  return ymdLocal(dt);
}

/** Last week, Sunday → Saturday — the team-reporting week convention (no ISO weeks). */
export function defaultRange(today = new Date()) {
  const dow = today.getDay();                       // 0 = Sunday
  const thisSunday = addDays(ymdLocal(today), -dow);
  const start = addDays(thisSunday, -7);
  return { start, end: addDays(start, 6) };
}

const isYmd = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ""));
const dayMs = (ymd) => Date.parse(`${ymd}T00:00:00Z`);

/** Inclusive day count, or null when either date is missing/invalid. */
export function spanDays(start, end) {
  if (!isYmd(start) || !isYmd(end)) return null;
  return Math.round((dayMs(end) - dayMs(start)) / 86400000) + 1;
}

/** null when the range is scannable; otherwise the message to show inline. */
export function rangeError(start, end) {
  if (!isYmd(start) || !isYmd(end)) return "Pick a start and an end date.";
  const days = spanDays(start, end);
  if (days < 1) return "Start must be on or before end.";
  if (days > MAX_RANGE_DAYS) return `That is ${days} days — the most a scan covers is ${MAX_RANGE_DAYS}.`;
  return null;
}

/** Which rows a chip shows. Dismissed rows leave every chip except Dismissed and All. */
export function filterByChip(items, chip) {
  return (items || []).filter((it) => {
    if (chip === "all") return true;
    if (chip === "dismissed") return !!it.dismissed;
    if (it.dismissed) return false;
    if (chip === "covered") return it.coverage?.status === "covered";
    return ATTENTION.has(it.coverage?.status);
  });
}

export function chipCounts(items) {
  return Object.fromEntries(CHIPS.map((c) => [c.id, filterByChip(items, c.id).length]));
}

/** [{ date, items }] in date order; items keep their server (start-time) order. */
export function groupByDate(items) {
  const map = new Map();
  (items || []).forEach((it) => {
    const d = it.dateLocal || "unknown";
    if (!map.has(d)) map.set(d, []);
    map.get(d).push(it);
  });
  return [...map.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([date, rows]) => ({ date, items: rows }));
}

export function fmtDateHeading(ymd) {
  if (!isYmd(ymd)) return ymd;
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" });
}

export function fmtDuration(mins) {
  const n = Number(mins) || 0;
  if (n < 60) return `${n}m`;
  const h = Math.floor(n / 60), m = n % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

export const externalAttendees = (item) => (item?.attendees || []).filter((a) => !a.internal);

const attendeeLine = (a) => (a.name && a.name.toLowerCase() !== a.email ? `${a.name} <${a.email}>` : a.email);

/**
 * What the To-Do ticket modal is opened with. Merged into defaultTicket AFTER
 * the existing defaults, so queue/priority/status/work type keep their rules.
 * `hoursWorked` is the meeting length to 2dp; the explicit UTC start/end and
 * the local date travel on the ticket state and are stripped before POST.
 */
export function buildTicketPrefill(item) {
  const ext = externalAttendees(item);
  const hours = Math.round(((Number(item.durationMins) || 0) / 60) * 100) / 100;
  return {
    title: item.subject || "",
    description: `Meeting: ${item.subject || ""}\n${item.dateLocal} ${item.startLocal}–${item.endLocal}\nAttendees: ${ext.map(attendeeLine).join(", ") || "—"}`,
    companyId: item.company?.id ?? "",
    contactId: item.contactId ?? "",
    hoursWorked: hours > 0 ? hours : "",
    timeEntryNotes: item.subject || "",
    timeStartUtc: item.startUtc,
    timeEndUtc: item.endUtc,
    timeDateLocal: item.dateLocal,
  };
}

/** Hours between two HH:MM local times on one day, to 2dp; 0 when end ≤ start. */
export function hoursBetween(startLocal, endLocal) {
  const [sh, sm] = String(startLocal || "").split(":").map(Number);
  const [eh, em] = String(endLocal || "").split(":").map(Number);
  if ([sh, sm, eh, em].some((n) => !Number.isFinite(n))) return 0;
  const mins = (eh * 60 + em) - (sh * 60 + sm);
  return mins > 0 ? Math.round((mins / 60) * 100) / 100 : 0;
}

/** Local date + HH:MM → UTC ISO without the Z (the shape /api/timelog/entry expects). */
export function localToUtcNoZ(dateLocal, hhmm) {
  const [y, m, d] = String(dateLocal || "").split("-").map(Number);
  const [h, mi] = String(hhmm || "").split(":").map(Number);
  if ([y, m, d, h, mi].some((n) => !Number.isFinite(n))) return null;
  return new Date(y, m - 1, d, h, mi, 0).toISOString().slice(0, 19);
}

/** The Add Time modal's starting values, in Perth local time from the meeting. */
export function buildAddTimePrefill(item) {
  return {
    dateLocal: item.dateLocal,
    startLocal: item.startLocal,
    endLocal: item.endLocal,
    hours: hoursBetween(item.startLocal, item.endLocal),
    summaryNotes: item.subject || "",
  };
}

/** Pure merge used by the tab after /check: swap one row's company + coverage, leave the rest untouched. */
export function applyCheck(items, key, { company, coverage }) {
  return (items || []).map((it) => (it.key === key
    ? { ...it, company, ambiguous: false, coverage, contactId: company?.id === it.company?.id ? it.contactId : null }
    : it));
}
